import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useOpenAccount } from '../../ApexOPL/AccountOpening/hooks/useOpenAccount';
import { ErrorState } from '../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { Box } from '../../CommonComponents/OPL/Box/Box';
import { Button } from '../../CommonComponents/OPL/Button/Button';
import { Card } from '../../CommonComponents/OPL/Card/Card';
import { FullPageLoading } from '../../CommonComponents/OPL/FullpageLoading/FullPageLoading';
import { Icon } from '../../CommonComponents/OPL/Icon/Icon';
import { Modal } from '../../CommonComponents/OPL/Modal/Modal';
import { ModalBody } from '../../CommonComponents/OPL/Modal/ModalBody';
import { ModalCloseButton } from '../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalFooter } from '../../CommonComponents/OPL/Modal/ModalFooter';
import { ModalHeader } from '../../CommonComponents/OPL/Modal/ModalHeader';
import { PdfViewer } from '../../CommonComponents/OPL/PdfViewer/PdfViewer';
import { useDocumentTitle } from '../../_hooks/useDocumentTitle';
import { Content, StickyFooter } from '../../CommonComponents/OPL/StickyFooter/StickyFooter';
import { Text } from '../../CommonComponents/OPL/Text/Text';
import { Tooltip } from '../../CommonComponents/OPL/Tooltip/Tooltip';
import { useResetModuleWrapperStyles } from '../../_hooks/useResetModuleWrapperStyles';
import { useAcceptAgreementMutation } from './api/useAcceptAgreementMutation';
import { AgreementDTO, useAutoAccountAgreementsQuery } from './api/useAutoAccountAgreementsQuery';
import { CancelModal } from './components/CancelModal/CancelModal';
import { useRouteParams } from './hooks/useRouteParams';

export const AutoAccountAgreements = () => {
  useDocumentTitle('Automated Account - Agreements');

  useResetModuleWrapperStyles();

  const { clientId } = useRouteParams();

  const [isCancelModalVisible, setCancelModalVisible] = useState(false);
  const [selectedAgreement, setSelectedAgreement] = useState<AgreementDTO>();
  const [selectedAgreementReachedEnd, setSelectedAgreementReachEnd] = useState(false);

  useEffect(() => {
    if (!selectedAgreement) {
      setSelectedAgreementReachEnd(false);
    }
  }, [selectedAgreement]);

  const { data, isLoading, isError, refetch } = useAutoAccountAgreementsQuery();

  const { onOpenAccount, isLoading: isLoadingOpenAccount } = useOpenAccount({ clientId });

  useEffect(() => {
    if (data?.length === 0) {
      onOpenAccount({ isFromWorkflow: true });
    }
  }, [data]);

  const hasAcceptedAllAgreements = useMemo(
    () => data?.every(agreement => agreement.accepted) ?? false,
    [data],
  );

  const {
    mutateAsync: acceptAgreement,
    isLoading: isAcceptingAgreement,
    reset: resetAcceptAgreement,
  } = useAcceptAgreementMutation();

  const handleAgreementModalClose = useCallback(() => {
    setSelectedAgreement(undefined);

    resetAcceptAgreement();
  }, [setSelectedAgreement, resetAcceptAgreement]);

  if (isLoading || isLoadingOpenAccount) {
    return (
      <Box mt={96}>
        <Content>
          <FullPageLoading text="Loading..." size="medium" />
        </Content>

        <StickyFooter />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box alignItems="center" display="flex" height="100%" justifyContent="center" width="100%">
        <ErrorState onAction={refetch} />
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <>
      <Box minWidth={864}>
        <Content>
          <Card maxWidth={640} mt={96} mx="auto">
            <Box p="24" display="flex" flexDirection="column">
              <Text color="neutral.700" fontWeight={500} fontSize="h3" mb="8">
                Agreements
              </Text>

              <Text fontWeight={500} mb="24" color="neutral.700">
                Please review and agree to the following agreements to proceed with opening the
                account.
              </Text>

              <Box display="flex" flexDirection="column" as="ul" css="list-style-type: none" p="0">
                {data.map((agreement, index) => (
                  <Box
                    as="li"
                    key={agreement.id}
                    mb={index !== data.length - 1 ? '24' : '0'}
                    display="flex"
                    alignItems="center"
                  >
                    <Icon
                      className={`${agreement.accepted ? 'fas' : 'far'} fa-check-circle`}
                      mr="8"
                      color={agreement.accepted ? 'success.500' : 'neutral.400'}
                    />
                    <Button variant="link" onClick={() => setSelectedAgreement(agreement)}>
                      {agreement.name}
                    </Button>
                  </Box>
                ))}
              </Box>
            </Box>
          </Card>
        </Content>

        {selectedAgreement && (
          <Modal isOpen onRequestClose={handleAgreementModalClose} centered size="large">
            <ModalHeader hasBorder>&nbsp;</ModalHeader>
            <ModalCloseButton onClick={handleAgreementModalClose} />

            <ModalBody css="padding: 0;">
              <Box height="60vh">
                <PdfViewer
                  url={selectedAgreement.url}
                  fileName={selectedAgreement.name}
                  onReachEnd={() => setSelectedAgreementReachEnd(true)}
                  displayOptions={{
                    showAnnotationTools: false,
                    showLeftHandPanel: false,
                    defaultViewMode: 'FIT_WIDTH',
                    dockPageControls: false,
                  }}
                />
              </Box>
            </ModalBody>

            <ModalFooter hasBorder>
              <Tooltip
                disabled={selectedAgreementReachedEnd || selectedAgreement.accepted}
                content="Please review the terms of the agreement prior to selecting I agree."
              >
                <span>
                  <Button
                    disabled={selectedAgreement.accepted || !selectedAgreementReachedEnd}
                    isLoading={isAcceptingAgreement}
                    onClick={async () => {
                      await acceptAgreement(selectedAgreement.id);

                      setSelectedAgreement(undefined);
                    }}
                  >
                    {selectedAgreement.accepted ? 'Accepted' : 'I Agree'}
                  </Button>
                </span>
              </Tooltip>
            </ModalFooter>
          </Modal>
        )}

        <StickyFooter>
          <Button
            variant="subtleInactive"
            size="large"
            onClick={() => {
              setCancelModalVisible(true);
            }}
            ml="16"
          >
            Cancel
          </Button>

          <Tooltip
            disabled={hasAcceptedAllAgreements}
            content="Please review the agreements to proceed."
            placement="top"
          >
            <span>
              <Button
                disabled={!hasAcceptedAllAgreements}
                variant="primary"
                size="large"
                onClick={async () => {
                  try {
                    await onOpenAccount({ isFromWorkflow: true });
                  } catch (err) {
                    console.error(err);
                  }
                }}
                ml="16"
              >
                Continue
              </Button>
            </span>
          </Tooltip>
        </StickyFooter>
      </Box>

      {isCancelModalVisible && <CancelModal onClose={() => setCancelModalVisible(false)} />}
    </>
  );
};
